import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, Grid, Button } from "@mui/material";
import { Build as BuildIcon } from "@mui/icons-material";
import { ParseResponse, jerseyApi } from "../../api/jerseyApi";
import PatternTransformFix from "./PatternTransformFix";

// Helper functions moved outside component to avoid dependency issues
const cleanSvgContent = (svgText: string): string => {
  try {
    // Remove any leading/trailing whitespace
    let cleaned = svgText.trim();

    // Handle namespaced SVG content (svg:svg -> svg)
    if (cleaned.includes("svg:svg")) {
      // Remove svg: namespace prefix from all elements
      cleaned = cleaned.replace(/svg:/g, "");
      // Remove namespace declarations that might cause issues
      cleaned = cleaned.replace(/xmlns:svg="[^"]*"/g, "");
      cleaned = cleaned.replace(/xmlns:ns\d+="[^"]*"/g, "");
      // Clean up any remaining namespace references
      cleaned = cleaned.replace(/ns\d+:/g, "");
    }

    // Ensure it starts with <?xml or <svg
    if (!cleaned.startsWith("<?xml") && !cleaned.startsWith("<svg")) {
      console.warn("Invalid SVG content received:", cleaned.substring(0, 100));
      return generateFallbackSVG("front", null);
    }

    // Basic validation - check if it contains essential SVG elements
    if (!cleaned.includes("<svg") || !cleaned.includes("</svg>")) {
      console.warn("SVG content missing essential elements");
      return generateFallbackSVG("front", null);
    }

    // Ensure the SVG has proper dimensions
    if (
      !cleaned.includes("viewBox") &&
      !cleaned.includes("width") &&
      !cleaned.includes("height")
    ) {
      // Add basic dimensions if missing
      cleaned = cleaned.replace(
        "<svg",
        '<svg width="200" height="300" viewBox="0 0 200 300"'
      );
    }

    return cleaned;
  } catch (error) {
    console.error("Error cleaning SVG content:", error);
    return generateFallbackSVG("front", null);
  }
};

const generateFallbackSVG = (
  view: "front" | "side",
  parseResult: ParseResponse | null
) => {
  const baseColor = parseResult?.base_color || "#ff0000";
  const elements = parseResult?.elements || [];

  // Convert color names to hex values
  const colorMap: { [key: string]: string } = {
    red: "#ff0000",
    blue: "#0000ff",
    green: "#00ff00",
    yellow: "#ffff00",
    white: "#ffffff",
    black: "#000000",
    purple: "#800080",
    orange: "#ffa500",
    pink: "#ffc0cb",
    brown: "#a52a2a",
  };

  const getColor = (colorName: string) => {
    return colorMap[colorName.toLowerCase()] || colorName;
  };

  const finalBaseColor = getColor(baseColor);
  const sleevesElement = elements.find((e) => e.element_type === "sleeves");
  const sleevesColor = sleevesElement
    ? getColor(sleevesElement.color || baseColor)
    : finalBaseColor;
  const capElement = elements.find((e) => e.element_type === "cap");
  const capColor = capElement
    ? getColor(capElement.color || baseColor)
    : finalBaseColor;

  return `<svg width="200" height="300" viewBox="0 0 200 300" xmlns="http://www.w3.org/2000/svg">
      <!-- Jersey base -->
      <path
        d="M50 50 L150 50 L150 80 L170 100 L170 250 L30 250 L30 100 Z"
        fill="${finalBaseColor}"
        stroke="#333"
        stroke-width="2"
      />
      ${
        sleevesElement
          ? `
        <ellipse cx="35" cy="120" rx="15" ry="40" fill="${sleevesColor}" stroke="#333" stroke-width="1" />
        <ellipse cx="165" cy="120" rx="15" ry="40" fill="${sleevesColor}" stroke="#333" stroke-width="1" />
      `
          : ""
      }
      ${
        capElement
          ? `
        <ellipse cx="100" cy="30" rx="25" ry="15" fill="${capColor}" stroke="#333" stroke-width="1" />
      `
          : ""
      }
      <text x="100" y="280" text-anchor="middle" fill="#666" font-size="12" font-family="Arial, sans-serif">
        ${view === "front" ? "Front View" : "Side View"}
      </text>
      <text x="100" y="295" text-anchor="middle" fill="#999" font-size="10" font-family="Arial, sans-serif">
        Simplified Preview
      </text>
    </svg>`;
};

interface SVGPreviewProps {
  description: string;
  parseResult?: ParseResponse | null;
  onSvgsReady?: (frontSvg: string, sideSvg: string) => void;
}

const SVGPreview: React.FC<SVGPreviewProps> = ({
  description,
  parseResult,
  onSvgsReady,
}) => {
  const [frontSvg, setFrontSvg] = useState<string | null>(null);
  const [sideSvg, setSideSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);

  // Notify parent when SVGs are ready or updated
  useEffect(() => {
    if (frontSvg && sideSvg && onSvgsReady) {
      onSvgsReady(frontSvg, sideSvg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontSvg, sideSvg]); // Don't include onSvgsReady to avoid infinite loop

  // const loadRealTemplates = useCallback(async () => {
  //   setLoading(true);

  //   try {
  //     // Try to get preview from backend first
  //     const previewData = {
  //       description: description,
  //       elements: parseResult?.elements || [],
  //       base_color: parseResult?.base_color
  //     };

  //     // Load front view
  //     const frontResponse = await fetch('/api/v1/preview/svg', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  //       },
  //       body: JSON.stringify({ ...previewData, view: 'front' })
  //     });

  //     if (frontResponse.ok) {
  //       const frontSvgText = await frontResponse.text();
  //       console.log('Raw front SVG received:', frontSvgText.substring(0, 200));
  //       // Clean and validate SVG content
  //       const cleanedSvg = cleanSvgContent(frontSvgText);
  //       console.log('Cleaned front SVG:', cleanedSvg.substring(0, 200));
  //       setFrontSvg(cleanedSvg);
  //     } else {
  //       console.log('Front preview failed, using fallback template');
  //       // Fallback to base template
  //       const frontTemplateResponse = await fetch('/api/v1/templates/base_front.svg');
  //       if (frontTemplateResponse.ok) {
  //         const frontTemplate = await frontTemplateResponse.text();
  //         const cleanedSvg = cleanSvgContent(frontTemplate);
  //         setFrontSvg(cleanedSvg);
  //       } else {
  //         setFrontSvg(generateFallbackSVG('front', parseResult || null));
  //       }
  //     }

  //     // Load side view
  //     const sideResponse = await fetch('/api/v1/preview/svg', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  //       },
  //       body: JSON.stringify({ ...previewData, view: 'side' })
  //     });

  //     if (sideResponse.ok) {
  //       const sideSvgText = await sideResponse.text();
  //       console.log('Raw side SVG received:', sideSvgText.substring(0, 200));
  //       // Clean and validate SVG content
  //       const cleanedSvg = cleanSvgContent(sideSvgText);
  //       console.log('Cleaned side SVG:', cleanedSvg.substring(0, 200));
  //       setSideSvg(cleanedSvg);
  //     } else {
  //       console.log('Side preview failed, using fallback template');
  //       // Fallback to base template
  //       const sideTemplateResponse = await fetch('/api/v1/templates/base_side.svg');
  //       if (sideTemplateResponse.ok) {
  //         const sideTemplate = await sideTemplateResponse.text();
  //         const cleanedSvg = cleanSvgContent(sideTemplate);
  //         setSideSvg(cleanedSvg);
  //       } else {
  //         setSideSvg(generateFallbackSVG('side', parseResult || null));
  //       }
  //     }

  //   } catch (error) {
  //     console.error('Error loading real templates:', error);
  //     // Fallback to simple SVG
  //     setFrontSvg(generateFallbackSVG('front', parseResult || null));
  //     setSideSvg(generateFallbackSVG('side', parseResult || null));
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [description, parseResult]);

  // Load real SVG templates when parseResult changes
  const loadRealTemplates = useCallback(async () => {
    console.log("🎨 Starting preview generation...");
    setLoading(true);

    try {
      const previewData = {
        description: description,
        elements: parseResult?.elements || [],
        base_color: parseResult?.base_color,
      };

      console.log("📤 Preview request data:", previewData);

      // Load front view
      try {
        const frontSvgText = await jerseyApi.generateSVGPreview({
          ...previewData,
          view: "front"
        });
        console.log(
          "✅ Front SVG received (first 200 chars):",
          frontSvgText.substring(0, 200)
        );
        const cleanedFrontSvg = cleanSvgContent(frontSvgText);
        console.log("🧹 Cleaned front SVG (first 200 chars):", cleanedFrontSvg.substring(0, 200));
        setFrontSvg(cleanedFrontSvg);
      } catch (error) {
        console.error("❌ Front preview failed:", error);
        setFrontSvg(generateFallbackSVG("front", parseResult || null));
      }

      // Load side view
      try {
        const sideSvgText = await jerseyApi.generateSVGPreview({
          ...previewData,
          view: "side"
        });
        console.log(
          "✅ Side SVG received (first 200 chars):",
          sideSvgText.substring(0, 200)
        );
        const cleanedSideSvg = cleanSvgContent(sideSvgText);
        console.log("🧹 Cleaned side SVG (first 200 chars):", cleanedSideSvg.substring(0, 200));
        setSideSvg(cleanedSideSvg);
      } catch (error) {
        console.error("❌ Side preview failed:", error);
        setSideSvg(generateFallbackSVG("side", parseResult || null));
      }
    } catch (error) {
      console.error("❌ Preview error:", error);
      // Set fallback SVGs on error
      setFrontSvg(generateFallbackSVG("front", parseResult || null));
      setSideSvg(generateFallbackSVG("side", parseResult || null));
    } finally {
      // Always set loading to false when done
      setLoading(false);
    }
  }, [description, parseResult]);

  useEffect(() => {
    if (parseResult && parseResult.elements.length > 0) {
      loadRealTemplates();
    } else {
      setFrontSvg(null);
      setSideSvg(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseResult]); // Only depend on parseResult, not loadRealTemplates

  if (loading) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="subtitle2" gutterBottom>
          Jersey Preview
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
            bgcolor: "grey.50",
            borderRadius: 1,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading real templates...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!parseResult || !parseResult.elements.length) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="subtitle2" gutterBottom>
          Jersey Preview
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
            bgcolor: "grey.100",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Enter a description to see jersey preview
          </Typography>
        </Box>
      </Box>
    );
  }

  // Debug logging
  console.log("🖼️ Rendering SVG Preview:", {
    frontSvg: frontSvg ? `${frontSvg.length} chars` : 'null',
    sideSvg: sideSvg ? `${sideSvg.length} chars` : 'null',
    loading,
    parseResult: parseResult?.elements?.length || 0
  });

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography variant="subtitle2" gutterBottom>
        Jersey Preview
      </Typography>

      <Grid container spacing={2}>
        {/* Front View */}
        <Grid item xs={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
              bgcolor: "grey.50",
              borderRadius: 1,
              p: 1,
            }}
          >
            {frontSvg ? (
              <div
                dangerouslySetInnerHTML={{ __html: frontSvg }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: generateFallbackSVG("front", parseResult || null),
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            )}
          </Box>
        </Grid>

        {/* Side View */}
        <Grid item xs={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
              bgcolor: "grey.50",
              borderRadius: 1,
              p: 1,
            }}
          >
            {sideSvg ? (
              <div
                dangerouslySetInnerHTML={{ __html: sideSvg }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: generateFallbackSVG("side", parseResult || null),
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        {frontSvg || sideSvg
          ? "Real jersey template preview"
          : "Simplified preview - real templates loading..."}
      </Typography>

      {/* Fix Patterns Button */}
      {(frontSvg || sideSvg) && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Button
            variant="outlined"
            startIcon={<BuildIcon />}
            onClick={() => setFixDialogOpen(true)}
            size="small"
          >
            Fix Patterns
          </Button>
        </Box>
      )}

      {/* Pattern Transform Fix Dialog */}
      <PatternTransformFix
        open={fixDialogOpen}
        onClose={() => setFixDialogOpen(false)}
        frontSvg={frontSvg}
        sideSvg={sideSvg}
        onSvgUpdate={(newFrontSvg, newSideSvg) => {
          setFrontSvg(newFrontSvg);
          setSideSvg(newSideSvg);
        }}
      />
    </Box>
  );
};

export default SVGPreview;
